import { loadStripe } from "@stripe/stripe-js";

export const csvFile = "/output.csv";
export const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
).catch((error) => {
  console.warn('Stripe failed to load:', error);
  return null;
});

export const datasets = [
  "advancementOccupations",
  "certifications",
  "commonSkills",
  "definingSkills",
  "demand",
  "distinguishingSkills",
  "education",
  "employers",
  "employment",
  "experience",
  "feederOccupations",
  "jobLevel",
  "lateralOccupations",
  "locationQuotient",
  "necessarySkills",
  "riskScore",
  "salary",
  "salaryBoostingSkills",
  "similarOccupations",
  "specializedSkills",
  "taxonomicalInfo",
  "titles",
  "typicalEducation",
];

export const assessmentData = [
  {
    section: "Basic AI Awareness",
    questions: [
      {
        question:
          "How familiar are you with the basic concepts of AI (e.g., machine learning, deep learning, natural language processing)?",
        hint: "A foundational understanding of these terms helps you recognize AI’s potential and limitations.",
        options: ["Not at all", "Slightly", "Moderately", "Very", "Extremely"],
      },
      {
        question:
          "How often do you actively use AI-powered applications (e.g., virtual assistants like Siri/Alexa, recommendation systems, chatbots)?",
        hint: "Regular exposure to AI tools builds comfort and intuition about how AI behaves.",
        options: [
          "Never",
          "Occasionally",
          "At least once a week",
          "Almost daily",
          "Daily and for multiple tasks",
        ],
      },
    ],
  },
  {
    section: "Data and Digital Literacy",
    questions: [
      {
        question:
          "How comfortable are you with organizing, analyzing, or interpreting data (spreadsheets, basic statistics, data visualizations)?",
        hint: "AI relies on data; understanding data basics helps you evaluate AI outputs and spot inaccuracies.",
        options: ["Not at all", "Slightly", "Moderately", "Very", "Extremely"],
      },
      {
        question:
          "How familiar are you with data privacy best practices (managing permissions, protecting sensitive information, GDPR/CCPA basics)?",
        hint: "Knowing how data is collected, stored, and protected is crucial in an AI-driven world.",
        options: ["Not at all", "Slightly", "Moderately", "Very", "Extremely"],
      },
    ],
  },
  {
    section: "Tech Savviness",
    questions: [
      {
        question:
          "Have you ever written code or scripts (in Python, R, JavaScript, or similar) to automate tasks or perform data analysis?",
        hint: "Even basic coding skills can accelerate your ability to explore and interact with AI tools.",
        options: [
          "Never",
          "Once or twice in a tutorial",
          "Occasionally for small projects",
          "Frequently and comfortably",
          "Regularly in a professional or advanced context",
        ],
      },
      {
        question:
          "Have you experimented with no-code or low-code AI platforms, or online AI demos (e.g., AI image generation, ChatGPT)?",
        hint: "Hands-on experience helps demystify AI and allows you to learn its capabilities/limitations quickly.",
        options: [
          "Never",
          "Rarely",
          "Occasionally",
          "Often",
          "Very frequently",
        ],
      },
    ],
  },
  {
    section: "Mindset and Adaptability",
    questions: [
      {
        question:
          "How open are you to continuously learning new technologies and adapting your workflow or skill set?",
        hint: "The AI field evolves rapidly; staying current requires ongoing education and adaptability.",
        options: [
          "Not willing",
          "Slightly willing",
          "Moderately willing",
          "Very willing",
          "Extremely willing",
        ],
      },
      {
        question:
          "When encountering a new AI tool or concept, do you tend to explore and experiment, or do you wait for detailed instructions and proven outcomes?",
        hint: "Being proactive and experimental can accelerate mastery of AI tools.",
        options: [
          "Not likely",
          "Slightly likely",
          "Moderately likely",
          "Very likely",
          "Extremely likely",
        ],
      },
    ],
  },
  {
    section: "Critical Thinking About AI",
    questions: [
      {
        question:
          "How aware are you of issues like AI bias, fairness, and potential misuse of AI technologies?",
        hint: "Recognizing and addressing AI’s ethical challenges builds trust and responsibility in AI usage.",
        options: [
          "Not at all aware",
          "Slightly aware",
          "Moderately aware",
          "Very aware",
          "Extremely aware",
        ],
      },
      {
        question:
          "How often do you question or verify the accuracy of AI outputs (e.g., checking chatbot responses, verifying recommendations)?",
        hint: "Critical evaluation prevents blind reliance on AI, reducing risks of misinformation or errors.",
        options: ["Never", "Rarely", "Occasionally", "Often", "Almost always"],
      },
    ],
  },
  {
    section: "Career and Upskilling",
    questions: [
      {
        question:
          "Are you aware of the variety of AI and data-related career paths (data scientist, ML engineer, prompt engineer, AI product manager)?",
        hint: "Awareness of emerging roles can guide your professional development or career transition.",
        options: [
          "Not informed",
          "Slightly informed",
          "Moderately informed",
          "Very informed",
          "Extremely informed",
        ],
      },
      {
        question:
          "Are you actively learning new skills (through online courses, workshops, or certifications) to enhance your AI literacy and skills?",
        hint: "Dedicated learning boosts your competitiveness and effectiveness in the AI-driven job market.",
        options: ["Not at all", "Slightly", "Moderately", "Very", "Extremely"],
      },
      {
        question:
          "Do you seek opportunities to integrate AI tools or features into your everyday tasks at work or in personal projects?",
        hint: "Practical application cements knowledge and increases efficiency.",
        options: ["Never", "Rarely", "Occasionally", "Often", "Almost always"],
      },
    ],
  },
  {
    section: "Future Vision",
    questions: [
      {
        question:
          "How well do you understand the potential impact of AI on your specific field or industry (e.g., automation, new services)?",
        hint: "Recognizing industry trends allows you to future-proof your career decisions and skill sets.",
        options: [
          "Do not understand",
          "Slightly understand",
          "Moderately understand",
          "Highly understand",
          "Significantly understand",
        ],
      },
      {
        question:
          "Overall, how confident do you feel about staying relevant, competitive, and adaptable as AI becomes more widespread?",
        hint: "Self-assessment of confidence can highlight gaps in knowledge or areas where you might want more training.",
        options: [
          "Not confident",
          "Slightly confident",
          "Moderately confident",
          "Very confident",
          "Extremely confident",
        ],
      },
    ],
  },
];

export const colors = ["#587DBD", "#E40891", "#1D1E9D", "#8979FF", "#3E80F9"];

export const genders = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
  { value: "other", label: "Other" },
];

export const countries = [
  { value: "Afghanistan", label: "Afghanistan" },
  { value: "Albania", label: "Albania" },
  { value: "Algeria", label: "Algeria" },
  { value: "Andorra", label: "Andorra" },
  { value: "Angola", label: "Angola" },
  { value: "Antigua and Barbuda", label: "Antigua and Barbuda" },
  { value: "Argentina", label: "Argentina" },
  { value: "Armenia", label: "Armenia" },
  { value: "Australia", label: "Australia" },
  { value: "Austria", label: "Austria" },
  { value: "Azerbaijan", label: "Azerbaijan" },
  { value: "Bahamas", label: "Bahamas" },
  { value: "Bahrain", label: "Bahrain" },
  { value: "Bangladesh", label: "Bangladesh" },
  { value: "Barbados", label: "Barbados" },
  { value: "Belarus", label: "Belarus" },
  { value: "Belgium", label: "Belgium" },
  { value: "Belize", label: "Belize" },
  { value: "Benin", label: "Benin" },
  { value: "Bhutan", label: "Bhutan" },
  { value: "Bolivia", label: "Bolivia" },
  { value: "Bosnia and Herzegovina", label: "Bosnia and Herzegovina" },
  { value: "Botswana", label: "Botswana" },
  { value: "Brazil", label: "Brazil" },
  { value: "Brunei", label: "Brunei" },
  { value: "Bulgaria", label: "Bulgaria" },
  { value: "Burkina Faso", label: "Burkina Faso" },
  { value: "Burundi", label: "Burundi" },
  { value: "Cabo Verde", label: "Cabo Verde" },
  { value: "Cambodia", label: "Cambodia" },
  { value: "Cameroon", label: "Cameroon" },
  { value: "Canada", label: "Canada" },
  { value: "Central African Republic", label: "Central African Republic" },
  { value: "Chad", label: "Chad" },
  { value: "Chile", label: "Chile" },
  { value: "China", label: "China" },
  { value: "Colombia", label: "Colombia" },
  { value: "Comoros", label: "Comoros" },
  { value: "Congo", label: "Congo" },
  { value: "Costa Rica", label: "Costa Rica" },
  { value: "Croatia", label: "Croatia" },
  { value: "Cuba", label: "Cuba" },
  { value: "Cyprus", label: "Cyprus" },
  { value: "Czech Republic", label: "Czech Republic" },
  { value: "Denmark", label: "Denmark" },
  { value: "Djibouti", label: "Djibouti" },
  { value: "Dominica", label: "Dominica" },
  { value: "Dominican Republic", label: "Dominican Republic" },
  { value: "Ecuador", label: "Ecuador" },
  { value: "Egypt", label: "Egypt" },
  { value: "El Salvador", label: "El Salvador" },
  { value: "Equatorial Guinea", label: "Equatorial Guinea" },
  { value: "Eritrea", label: "Eritrea" },
  { value: "Estonia", label: "Estonia" },
  { value: "Eswatini", label: "Eswatini" },
  { value: "Ethiopia", label: "Ethiopia" },
  { value: "Fiji", label: "Fiji" },
  { value: "Finland", label: "Finland" },
  { value: "France", label: "France" },
  { value: "Gabon", label: "Gabon" },
  { value: "Gambia", label: "Gambia" },
  { value: "Georgia", label: "Georgia" },
  { value: "Germany", label: "Germany" },
  { value: "Ghana", label: "Ghana" },
  { value: "Greece", label: "Greece" },
  { value: "Grenada", label: "Grenada" },
  { value: "Guatemala", label: "Guatemala" },
  { value: "Guinea", label: "Guinea" },
  { value: "Guinea-Bissau", label: "Guinea-Bissau" },
  { value: "Guyana", label: "Guyana" },
  { value: "Haiti", label: "Haiti" },
  { value: "Honduras", label: "Honduras" },
  { value: "Hungary", label: "Hungary" },
  { value: "Iceland", label: "Iceland" },
  { value: "India", label: "India" },
  { value: "Indonesia", label: "Indonesia" },
  { value: "Iran", label: "Iran" },
  { value: "Iraq", label: "Iraq" },
  { value: "Ireland", label: "Ireland" },
  { value: "Israel", label: "Israel" },
  { value: "Italy", label: "Italy" },
  { value: "Jamaica", label: "Jamaica" },
  { value: "Japan", label: "Japan" },
  { value: "Jordan", label: "Jordan" },
  { value: "Kazakhstan", label: "Kazakhstan" },
  { value: "Kenya", label: "Kenya" },
  { value: "Kiribati", label: "Kiribati" },
  { value: "Kuwait", label: "Kuwait" },
  { value: "Kyrgyzstan", label: "Kyrgyzstan" },
  { value: "Laos", label: "Laos" },
  { value: "Latvia", label: "Latvia" },
  { value: "Lebanon", label: "Lebanon" },
  { value: "Lesotho", label: "Lesotho" },
  { value: "Liberia", label: "Liberia" },
  { value: "Libya", label: "Libya" },
  { value: "Liechtenstein", label: "Liechtenstein" },
  { value: "Lithuania", label: "Lithuania" },
  { value: "Luxembourg", label: "Luxembourg" },
  { value: "Madagascar", label: "Madagascar" },
  { value: "Malawi", label: "Malawi" },
  { value: "Malaysia", label: "Malaysia" },
  { value: "Maldives", label: "Maldives" },
  { value: "Mali", label: "Mali" },
  { value: "Malta", label: "Malta" },
  { value: "Marshall Islands", label: "Marshall Islands" },
  { value: "Mauritania", label: "Mauritania" },
  { value: "Mauritius", label: "Mauritius" },
  { value: "Mexico", label: "Mexico" },
  { value: "Micronesia", label: "Micronesia" },
  { value: "Moldova", label: "Moldova" },
  { value: "Monaco", label: "Monaco" },
  { value: "Mongolia", label: "Mongolia" },
  { value: "Montenegro", label: "Montenegro" },
  { value: "Morocco", label: "Morocco" },
  { value: "Mozambique", label: "Mozambique" },
  { value: "Myanmar", label: "Myanmar" },
  { value: "Namibia", label: "Namibia" },
  { value: "Nauru", label: "Nauru" },
  { value: "Nepal", label: "Nepal" },
  { value: "Netherlands", label: "Netherlands" },
  { value: "New Zealand", label: "New Zealand" },
  { value: "Nicaragua", label: "Nicaragua" },
  { value: "Niger", label: "Niger" },
  { value: "Nigeria", label: "Nigeria" },
  { value: "North Korea", label: "North Korea" },
  { value: "North Macedonia", label: "North Macedonia" },
  { value: "Norway", label: "Norway" },
  { value: "Oman", label: "Oman" },
  { value: "Pakistan", label: "Pakistan" },
  { value: "Palestine", label: "Palestine" },
  { value: "Palau", label: "Palau" },
  { value: "Panama", label: "Panama" },
  { value: "Papua New Guinea", label: "Papua New Guinea" },
  { value: "Paraguay", label: "Paraguay" },
  { value: "Peru", label: "Peru" },
  { value: "Philippines", label: "Philippines" },
  { value: "Poland", label: "Poland" },
  { value: "Portugal", label: "Portugal" },
  { value: "Qatar", label: "Qatar" },
  { value: "Romania", label: "Romania" },
  { value: "Russia", label: "Russia" },
  { value: "Rwanda", label: "Rwanda" },
  { value: "Saint Kitts and Nevis", label: "Saint Kitts and Nevis" },
  { value: "Saint Lucia", label: "Saint Lucia" },
  {
    value: "Saint Vincent and the Grenadines",
    label: "Saint Vincent and the Grenadines",
  },
  { value: "Samoa", label: "Samoa" },
  { value: "San Marino", label: "San Marino" },
  { value: "Sao Tome and Principe", label: "Sao Tome and Principe" },
  { value: "Saudi Arabia", label: "Saudi Arabia" },
  { value: "Senegal", label: "Senegal" },
  { value: "Serbia", label: "Serbia" },
  { value: "Seychelles", label: "Seychelles" },
  { value: "Sierra Leone", label: "Sierra Leone" },
  { value: "Singapore", label: "Singapore" },
  { value: "Slovakia", label: "Slovakia" },
  { value: "Slovenia", label: "Slovenia" },
  { value: "Solomon Islands", label: "Solomon Islands" },
  { value: "Somalia", label: "Somalia" },
  { value: "South Africa", label: "South Africa" },
  { value: "South Korea", label: "South Korea" },
  { value: "South Sudan", label: "South Sudan" },
  { value: "Spain", label: "Spain" },
  { value: "Sri Lanka", label: "Sri Lanka" },
  { value: "Sudan", label: "Sudan" },
  { value: "Suriname", label: "Suriname" },
  { value: "Sweden", label: "Sweden" },
  { value: "Switzerland", label: "Switzerland" },
  { value: "Syria", label: "Syria" },
  { value: "Taiwan", label: "Taiwan" },
  { value: "Tajikistan", label: "Tajikistan" },
  { value: "Tanzania", label: "Tanzania" },
  { value: "Thailand", label: "Thailand" },
  { value: "Timor-Leste", label: "Timor-Leste" },
  { value: "Togo", label: "Togo" },
  { value: "Tonga", label: "Tonga" },
  { value: "Trinidad and Tobago", label: "Trinidad and Tobago" },
  { value: "Tunisia", label: "Tunisia" },
  { value: "Turkey", label: "Turkey" },
  { value: "Turkmenistan", label: "Turkmenistan" },
  { value: "Tuvalu", label: "Tuvalu" },
  { value: "Uganda", label: "Uganda" },
  { value: "Ukraine", label: "Ukraine" },
  { value: "United Arab Emirates", label: "United Arab Emirates" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "United States", label: "United States" },
  { value: "Uruguay", label: "Uruguay" },
  { value: "Uzbekistan", label: "Uzbekistan" },
  { value: "Vanuatu", label: "Vanuatu" },
  { value: "Venezuela", label: "Venezuela" },
  { value: "Vietnam", label: "Vietnam" },
  { value: "Yemen", label: "Yemen" },
  { value: "Zambia", label: "Zambia" },
  { value: "Zimbabwe", label: "Zimbabwe" },
];

export const race = [
  { value: "white", label: "White" },
  {
    value: "black_or_african_american",
    label: "Black or African American",
  },
  { value: "asian", label: "Asian" },
  {
    value: "american_indian_or_alaska_native",
    label: "American Indian or Alaska Native",
  },
  {
    value: "native_hawaiian_or_pacific_islander",
    label: "Native Hawaiian or Other Pacific Islander",
  },
  { value: "two_or_more_races", label: "Two or More Races" },
  { value: "other_race", label: "Other Race" },
  {
    value: "hispanic_or_latino",
    label: "Hispanic or Latino (Ethnicity)",
  },
  {
    value: "prefer_not_to_say",
    label: "Prefer not to say",
  },
];

export const education = [
  {
    value: "less_than_high_school",
    label: "Less than High School Diploma",
  },
  {
    value: "high_school",
    label: "High School Diploma or Equivalent",
  },
  { value: "some_college", label: "Some College, No Degree" },
  { value: "associates_degree", label: "Associate's Degree" },
  { value: "bachelors_degree", label: "Bachelor's Degree" },
  { value: "masters_degree", label: "Master's Degree" },
  {
    value: "doctoral_or_professional",
    label: "Doctoral or Professional Degree",
  },
];

export const status = [
  { value: "student_new_graduate", label: "Student/New Graduate" },
  { value: "currently_employed", label: "Currently Employed" },
  {
    value: "looking_for_employment",
    label: "Looking for Employment",
  },
];

export const experience = [
  { value: "student", label: "Student" },
  { value: "entry_level", label: "Entry Level" },
  { value: "mid_career", label: "Mid Career" },
  { value: "senior_level", label: "Senior Level" },
  { value: "c_suite", label: "C Suite" },
];

export const workYears = [
  { value: "no_experience", label: "No Experience" },
  { value: "less_than_1_year", label: "Less than 1 Year" },
  { value: "1_2_years", label: "1-2 Years" },
  { value: "3_5_years", label: "3-5 Years" },
  { value: "6_9_years", label: "6-9 Years" },
  { value: "10_plus_years", label: "10+ Years" },
];

export const industry = [
  {
    value: "agriculture_forestry_fishing_hunting",
    label: "Agriculture, Forestry, Fishing, and Hunting",
  },
  {
    value: "mining_oil_gas_extraction",
    label: "Mining, Quarrying, and Oil and Gas Extraction",
  },
  { value: "utilities", label: "Utilities" },
  { value: "construction", label: "Construction" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "wholesale_trade", label: "Wholesale Trade" },
  { value: "retail_trade", label: "Retail Trade" },
  {
    value: "transportation_warehousing",
    label: "Transportation and Warehousing",
  },
  { value: "information", label: "Information" },
  { value: "finance_insurance", label: "Finance and Insurance" },
  {
    value: "real_estate_rental_leasing",
    label: "Real Estate and Rental and Leasing",
  },
  {
    value: "professional_scientific_technical_services",
    label: "Professional, Scientific, and Technical Services",
  },
  {
    value: "management_of_companies",
    label: "Management of Companies and Enterprises",
  },
  {
    value: "administrative_support_waste_management",
    label:
      "Administrative and Support and Waste Management and Remediation Services",
  },
  { value: "educational_services", label: "Educational Services" },
  {
    value: "health_care_social_assistance",
    label: "Health Care and Social Assistance",
  },
  {
    value: "arts_entertainment_recreation",
    label: "Arts, Entertainment, and Recreation",
  },
  {
    value: "accommodation_food_services",
    label: "Accommodation and Food Services",
  },
  {
    value: "other_services",
    label: "Other Services (except Public Administration)",
  },
  { value: "public_administration", label: "Public Administration" },
];

export const skills = [
  { value: "active_listening", label: "Active Listening" },
  { value: "oral_communication", label: "Oral Communication" },
  { value: "written_communication", label: "Written Communication" },
  { value: "java", label: "Java" },
  { value: "python", label: "Python" },
  { value: "sql", label: "SQL" },
  { value: "project_management", label: "Project Management" },
  { value: "team_management", label: "Team Management" },
  { value: "time_management", label: "Time Management" },
  { value: "compTIA_security_plus", label: "CompTIA Security+" },
  {
    value: "aws_certified_solutions_architect",
    label: "AWS Certified Solutions Architect",
  },
];

export const immigration = [
  {
    value: "new_immigrant_1_5_years",
    label: "New Immigrant (1-5 years)",
  },
  { value: "na", label: "N/A" },
];

export const quizStatus = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const ageGroup = [
  {
    value: "18-25",
    label: "18-25",
  },
  {
    value: "26-40",
    label: "26-40",
  },
  {
    value: "41-55",
    label: "41-55",
  },
  {
    value: "+55",
    label: "+55",
  },
];

export const usaStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

export const canadaProvinces = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

export const courseProgress = [
  { value: "Started", label: "Started" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

export const highAutomationColors = [
  "#d73027", // Deep Red
  "#f03b20", // Bright Red
  "#fc4e2a", // Reddish Orange
  "#fd8d3c", // Orange
  "#feb24c", // Light Orange
  "#fed976", // Yellow-Orange
  "#ffea00", // Yellow
  "#ffe600", // Bright Yellow
  "#ffd700", // Gold Yellow
  "#ffcc00", // Goldenrod
];

export const lowAutomationColors = [
  "#00441b", // Very Dark Green
  "#006d2c", // Deep Green
  "#238b45", // Dark Green
  "#41ab5d", // Medium Green
  "#66c2a4", // Soft Green
  "#78c679", // Light Soft Green
  "#9ccc65", // Light Green
  "#b2df8a", // Pale Green
  "#c2e699", // Light Greenish Yellow
  "#d9f0a3", // Very Light Green
];

export const AILiteracySkills = [
  {
    value: "understanding_ai",
    label: "Understanding What AI Is and Isn't",
  },
  {
    value: "ai_terminology",
    label: "AI Terminology: Models, Algorithms, Datasets, Training, Inference",
  },
  {
    value: "ethics_of_ai",
    label: "Ethics of AI: Bias, Fairness, Privacy, Transparency",
  },
  {
    value: "impact_of_ai",
    label: "Impact of AI and Adapting to AI",
  },
];

export const appliedAISkills = [
  {
    value: "effective_prompts",
    label: "Writing Effective Prompts for ChatGPT, Gemini, Claude",
  },
  {
    value: "ai_product_development",
    label: "AI Product Development",
  },
  {
    value: "data_storytelling",
    label: "Data Storytelling",
  },
  {
    value: "ai_tool_selection",
    label:
      "AI Tool Selection: Evaluating and Choosing Appropriate AI Tools for Specific Tasks",
  },
  {
    value: "ai_solution_architecture",
    label:
      "AI Solution Architecture: Designing End-to-End AI Systems for Business Problems",
  },
  {
    value: "ai_assisted_content_creation",
    label:
      "AI-Assisted Content Creation: Using AI Tools for Writing, Design, and Multimedia",
  },
];

export const ResponsibleAI = [
  {
    value: "bias_fairness_ai",
    label: "Bias & Fairness in AI Models",
  },
  {
    value: "explainable_ai",
    label: "Explainable AI (XAI)",
  },
  {
    value: "ai_regulations",
    label: "AI Regulations (GDPR, AI Act)",
  },
  {
    value: "ethics_of_ai",
    label: "Ethics of AI: Bias, Fairness, Privacy, Transparency",
  },
  {
    value: "human_ai_collaboration",
    label: "Human-AI Collaboration: Working Effectively Alongside AI Systems",
  },
];

export const MachineLearningDeepLearningPlatforms = [
  {
    value: "tensorFlow",
    label: "TensorFlow",
  },
  {
    value: "pyTorch",
    label: "PyTorch",
  },
  {
    value: "scikit-learn",
    label: "Scikit-learn",
  },
  {
    value: "keras",
    label: "Keras",
  },
  {
    value: "xGBoost_lightGBM",
    label: "XGBoost / LightGBM",
  },
];

export const AutoMLNoCodeLowCodeAIPlatforms = [
  {
    value: "google_autoML",
    label: "Google AutoML",
  },
  {
    value: "microsoft_azure_ML_studio",
    label: "Microsoft Azure ML Studio",
  },
  {
    value: "amazon_sageMaker",
    label: "Amazon SageMaker",
  },
  {
    value: "data_robot",
    label: "DataRobot",
  },
  {
    value: "teachable_machine",
    label: "Teachable Machine",
  },
  {
    value: "lobe.ai",
    label: "Lobe.ai",
  },
];

export const NaturalLanguageProcessingTools = [
  {
    value: "openAI_GPT",
    label: "OpenAI GPT (e.g., ChatGPT)",
  },
  {
    value: "hugging_face_transformers",
    label: "Hugging Face Transformers",
  },
  {
    value: "spaCy",
    label: "spaCy",
  },
  {
    value: "NLTK",
    label: "NLTK",
  },
  {
    value: "rasa",
    label: "Rasa",
  },
];

export const ComputerVisionTools = [
  {
    value: "openCV",
    label: "OpenCV",
  },
  {
    value: "YOLO",
    label: "YOLO",
  },
  {
    value: "faster_R-CNN",
    label: "Faster R-CNN",
  },
  {
    value: "labelbox",
    label: "Labelbox",
  },
];

export const GenerativeAITools = [
  {
    value: "dALL·E",
    label: "DALL·E",
  },
  {
    value: "runway_ML",
    label: "Runway ML",
  },
  {
    value: "midjourney",
    label: "Midjourney",
  },
  {
    value: "adobe_firefly",
    label: "Adobe Firefly",
  },
  {
    value: "descript",
    label: "Descript",
  },
];

export const MLOpsDeploymentTools = [
  {
    value: "mLflow",
    label: "mLflow",
  },
  {
    value: "weights_&_biases",
    label: "Weights & Biases",
  },
  {
    value: "docker",
    label: "Docker",
  },
  {
    value: "kubeFlow",
    label: "KubeFlow",
  },
  {
    value: "fastAPI_/_flask",
    label: "FastAPI / Flask",
  },
];

export const DataScienceVisualizationTools = [
  {
    value: "pandas",
    label: "pandas",
  },
  {
    value: "numPy",
    label: "NumPy",
  },
  {
    value: "tableau",
    label: "Tableau",
  },
  {
    value: "power_BI",
    label: "Power BI",
  },
  {
    value: "plotly_/_dash",
    label: "Plotly / DashPy",
  },
];

export const aiTools = [
  {
    value: "tensorflow",
    label: "TensorFlow",
  },
  {
    value: "pytorch",
    label: "PyTorch",
  },
  {
    value: "scikit_learn",
    label: "Scikit-learn",
  },
  {
    value: "keras",
    label: "Keras",
  },
  {
    value: "xgboost_lightgbm",
    label: "XGBoost / LightGBM",
  },
  {
    value: "google_automl",
    label: "Google AutoML",
  },
  {
    value: "microsoft_azure_ml_studio",
    label: "Microsoft Azure ML Studio",
  },
  {
    value: "amazon_sagemaker",
    label: "Amazon SageMaker",
  },
  {
    value: "datarobot",
    label: "DataRobot",
  },
  {
    value: "teachable_machine",
    label: "Teachable Machine",
  },
  {
    value: "lobe_ai",
    label: "Lobe.ai",
  },
  {
    value: "openai_gpt",
    label: "OpenAI GPT (e.g., ChatGPT)",
  },
  {
    value: "huggingface_transformers",
    label: "Hugging Face Transformers",
  },
  {
    value: "spacy",
    label: "spaCy",
  },
  {
    value: "nltk",
    label: "NLTK",
  },
  {
    value: "rasa",
    label: "Rasa",
  },
  {
    value: "opencv",
    label: "OpenCV",
  },
  {
    value: "yolo",
    label: "YOLO",
  },
  {
    value: "faster_rcnn",
    label: "Faster R-CNN",
  },
  {
    value: "labelbox",
    label: "Labelbox",
  },
  {
    value: "dalle",
    label: "DALL·E",
  },
  {
    value: "runway_ml",
    label: "Runway ML",
  },
  {
    value: "midjourney",
    label: "Midjourney",
  },
  {
    value: "adobe_firefly",
    label: "Adobe Firefly",
  },
  {
    value: "descript",
    label: "Descript",
  },
  {
    value: "mlflow",
    label: "MLflow",
  },
  {
    value: "weights_biases",
    label: "Weights & Biases",
  },
  {
    value: "docker",
    label: "Docker",
  },
  {
    value: "kubeflow",
    label: "KubeFlow",
  },
  {
    value: "fastapi_flask",
    label: "FastAPI / Flask",
  },
  {
    value: "pandas",
    label: "pandas",
  },
  {
    value: "numpy",
    label: "NumPy",
  },
  {
    value: "tableau",
    label: "Tableau",
  },
  {
    value: "power_bi",
    label: "Power BI",
  },
  {
    value: "plotly_dash",
    label: "Plotly / Dash",
  },
];

export const aiSkills = [
  {
    value: "critical_thinking",
    label:
      "Critical Thinking: Evaluating AI Outputs and Knowing When Human Judgment Is Needed",
  },
  {
    value: "creative_thinking",
    label: "Creative Thinking / Innovation",
  },
  {
    value: "communication_collaboration",
    label: "Communication and Collaboration",
  },
  {
    value: "problem_solving_with_ai",
    label: "Problem Solving with AI",
  },
  {
    value: "responsible_ai",
    label:
      "AI Ethics & Responsible AI: Understanding Implications and Potential Biases",
  },
  {
    value: "ai_project_management",
    label: "AI Project Management: Planning and Executing AI Initiatives",
  },
  {
    value: "ai_roi_assessment",
    label: "AI ROI Assessment: Measuring the Business Impact of AI Investments",
  },
  {
    value: "regulatory_compliance",
    label:
      "Regulatory Compliance: Staying Updated on AI-Related Laws and Regulations",
  },
  {
    value: "ai_strategy_development",
    label: "AI Strategy Development and Use Case Identification",
  },
  {
    value: "ml_fundamentals",
    label: "Machine Learning (ML) Fundamentals",
  },
  {
    value: "learning_types",
    label: "Supervised, Unsupervised, and Reinforcement Learning",
  },
  {
    value: "feature_engineering",
    label: "Feature Engineering & Model Evaluation",
  },
  {
    value: "deep_learning",
    label: "Deep Learning",
  },
  {
    value: "neural_networks",
    label: "Neural Networks (CNNs, RNNs, Transformers)",
  },
  {
    value: "ml_frameworks",
    label: "Frameworks: TensorFlow, PyTorch, Keras",
  },
  {
    value: "nlp",
    label: "Natural Language Processing (NLP)",
  },
  {
    value: "text_processing",
    label:
      "Text Preprocessing, Sentiment Analysis, Named Entity Recognition (NER)",
  },
  {
    value: "large_language_models",
    label: "Large Language Models (LLMs) like GPT, BERT, Llama",
  },
  {
    value: "computer_vision",
    label: "Computer Vision",
  },
  {
    value: "image_detection",
    label: "Image Classification, Object Detection (YOLO, Faster R-CNN)",
  },
  {
    value: "opencv_pillow",
    label: "OpenCV, Pillow",
  },
  {
    value: "probability_statistics",
    label: "Probability & Statistics (Bayesian Inference, Hypothesis Testing)",
  },
  {
    value: "model_algorithms",
    label: "Model Algorithms",
  },
  {
    value: "model_training",
    label: "Model Training and Tuning",
  },
  {
    value: "model_testing",
    label: "Model Testing",
  },
  {
    value: "model_deployment",
    label: "Model Deployment",
  },
  {
    value: "python",
    label: "Python",
  },
  {
    value: "python_libraries",
    label: "Libraries and Frameworks",
  },
  {
    value: "r_language",
    label: "R (For Statistical AI Models)",
  },
  {
    value: "ai_cloud_services",
    label: "APIs & Cloud AI Services (AWS SageMaker, Google Vertex AI)",
  },
  {
    value: "mlops",
    label: "MLOps (Model Deployment & Scaling)",
  },
  {
    value: "devops_tools",
    label: "Docker, Kubernetes, CI/CD Pipelines",
  },
  {
    value: "data_cleaning",
    label: "Data Preprocessing & Cleaning",
  },
  {
    value: "eda",
    label: "Exploratory Data Analysis (EDA)",
  },
  {
    value: "big_data",
    label: "Big Data Technologies (Hadoop, Spark)",
  },
  {
    value: "databases",
    label: "SQL & NoSQL Databases",
  },
  {
    value: "cloud_computing",
    label: "Cloud Computing",
  },
  {
    value: "cybersecurity_basics",
    label: "Cybersecurity Basics",
  },
  {
    value: "llm_fine_tuning",
    label: "Fine-Tuning LLMs",
  },
  {
    value: "autonomous_agents",
    label: "Autonomous Agents (AI Agents, AutoGPT)",
  },
  {
    value: "multimodal_ai",
    label: "Multimodal AI (Text + Image + Audio Models)",
  },
  {
    value: "quantum_ml",
    label: "Quantum Machine Learning",
  },
  {
    value: "rag",
    label: "Retrieval-Augmented Generation (RAG)",
  },
  {
    value: "no_code_ai",
    label: "No-Code/Low-Code AI Tools",
  },
];

export const softAISkills = [
  {
    value: "critical_thinking",
    label:
      "Critical Thinking: Evaluating AI Outputs and Knowing When Human Judgment Is Needed",
  },
  {
    value: "creative_thinking",
    label: "Creative Thinking / Innovation",
  },
  {
    value: "communication_collaboration",
    label: "Communication and Collaboration",
  },
  {
    value: "problem_solving_with_ai",
    label: "Problem Solving with AI",
  },
];

export const strategicAISkills = [
  {
    value: "responsible_ai",
    label:
      "AI Ethics & Responsible AI: Understanding Implications and Potential Biases",
  },
  {
    value: "ai_project_management",
    label: "AI Project Management: Planning and Executing AI Initiatives",
  },
  {
    value: "ai_roi_assessment",
    label: "AI ROI Assessment: Measuring the Business Impact of AI Investments",
  },
  {
    value: "regulatory_compliance",
    label:
      "Regulatory Compliance: Staying Updated on AI-Related Laws and Regulations",
  },
  {
    value: "ai_strategy_development",
    label: "AI Strategy Development and Use Case Identification",
  },
];

export const technicalAISkills = [
  {
    value: "ml_fundamentals",
    label: "Machine Learning (ML) Fundamentals",
  },
  {
    value: "learning_types",
    label: "Supervised, Unsupervised, and Reinforcement Learning",
  },
  {
    value: "feature_engineering",
    label: "Feature Engineering & Model Evaluation",
  },
  {
    value: "deep_learning",
    label: "Deep Learning",
  },
  {
    value: "neural_networks",
    label: "Neural Networks (CNNs, RNNs, Transformers)",
  },
  {
    value: "ml_frameworks",
    label: "Frameworks: TensorFlow, PyTorch, Keras",
  },
  {
    value: "nlp",
    label: "Natural Language Processing (NLP)",
  },
  {
    value: "text_processing",
    label:
      "Text Preprocessing, Sentiment Analysis, Named Entity Recognition (NER)",
  },
  {
    value: "large_language_models",
    label: "Large Language Models (LLMs) like GPT, BERT, Llama",
  },
  {
    value: "computer_vision",
    label: "Computer Vision",
  },
  {
    value: "image_detection",
    label: "Image Classification, Object Detection (YOLO, Faster R-CNN)",
  },
  {
    value: "opencv_pillow",
    label: "OpenCV, Pillow",
  },
  {
    value: "probability_statistics",
    label: "Probability & Statistics (Bayesian Inference, Hypothesis Testing)",
  },
  {
    value: "model_algorithms",
    label: "Model Algorithms",
  },
  {
    value: "model_training",
    label: "Model Training and Tuning",
  },
  {
    value: "model_testing",
    label: "Model Testing",
  },
  {
    value: "model_deployment",
    label: "Model Deployment",
  },
  {
    value: "python",
    label: "Python",
  },
  {
    value: "python_libraries",
    label: "Libraries and Frameworks",
  },
  {
    value: "r_language",
    label: "R (For Statistical AI Models)",
  },
  {
    value: "ai_cloud_services",
    label: "APIs & Cloud AI Services (AWS SageMaker, Google Vertex AI)",
  },
  {
    value: "mlops",
    label: "MLOps (Model Deployment & Scaling)",
  },
  {
    value: "devops_tools",
    label: "Docker, Kubernetes, CI/CD Pipelines",
  },
  {
    value: "data_cleaning",
    label: "Data Preprocessing & Cleaning",
  },
  {
    value: "eda",
    label: "Exploratory Data Analysis (EDA)",
  },
  {
    value: "big_data",
    label: "Big Data Technologies (Hadoop, Spark)",
  },
  {
    value: "databases",
    label: "SQL & NoSQL Databases",
  },
  {
    value: "cloud_computing",
    label: "Cloud Computing",
  },
  {
    value: "cybersecurity_basics",
    label: "Cybersecurity Basics",
  },
  {
    value: "llm_fine_tuning",
    label: "Fine-Tuning LLMs",
  },
  {
    value: "autonomous_agents",
    label: "Autonomous Agents (AI Agents, AutoGPT)",
  },
  {
    value: "multimodal_ai",
    label: "Multimodal AI (Text + Image + Audio Models)",
  },
  {
    value: "quantum_ml",
    label: "Quantum Machine Learning",
  },
  {
    value: "rag",
    label: "Retrieval-Augmented Generation (RAG)",
  },
  {
    value: "no_code_ai",
    label: "No-Code/Low-Code AI Tools",
  },
];

export const EmergingAI = [
  { value: "ai_agent_developer", label: "AI Agent Developer" },
];
export const aiCareerTitles = [
  { value: "ai_ml_engineer", label: "AI/ML Engineer" },
  { value: "llm_fine_tuning_expert", label: "LLM Fine-Tuning Expert" },
  { value: "deep_learning_engineer", label: "Deep Learning Engineer" },
  { value: "computer_vision_engineer", label: "Computer Vision Engineer" },
  {
    value: "nlp_engineer",
    label: "Natural Language Processing (NLP) Engineer",
  },
  {
    value: "neural_architecture_designer",
    label: "Neural Architecture Designer",
  },
  { value: "synthetic_data_specialist", label: "Synthetic Data Specialist" },
  { value: "ai_research_scientist", label: "AI Research Scientist" },
  { value: "ai_explainability_engineer", label: "AI Explainability Engineer" },
  { value: "ai_prompt_engineer", label: "AI Prompt Engineer" },
  { value: "generative_ai_engineer", label: "Generative AI Engineer" },
  {
    value: "autonomous_systems_engineer",
    label: "Autonomous Systems Engineer",
  },
  { value: "ai_developer_advocate", label: "AI Developer Advocate" },
  { value: "mlops_engineer", label: "MLOps Engineer" },
  {
    value: "ai_infrastructure_architect",
    label: "AI Infrastructure Architect",
  },
  { value: "robotics_ai_engineer", label: "Robotics AI Engineer" },
  { value: "ai_solutions_architect", label: "AI Solutions Architect" },
  { value: "data_engineer_ai_focused", label: "Data Engineer (AI-focused)" },
  { value: "ai_ethicist", label: "AI Ethicist" },
  { value: "responsible_ai_specialist", label: "Responsible AI Specialist" },
  { value: "ai_ethics_auditor", label: "AI Ethics Auditor" },
  { value: "ai_safety_researcher", label: "AI Safety Researcher" },
  { value: "ai_business_translator", label: "AI Business Translator" },
  { value: "ai_product_manager", label: "AI Product Manager" },
  { value: "ai_project_manager", label: "AI Project  Manager" },
  { value: "ai_ux_designer", label: "AI UX Designer" },
  { value: "ai_sales_engineer", label: "AI Sales Engineer" },
  { value: "ai_adoption_strategist", label: "AI Adoption Strategist:" },
  { value: "ai_strategy_consultant", label: "AI Strategy Consultant" },
  { value: "ai_systems_integrator", label: "AI Systems Integrator" },
  { value: "ai_compliance_manager", label: "AI Compliance Manager" },
  { value: "ai_business_analyst", label: "AI Business Analyst" },
  { value: "ai_trainer_annotator", label: "AI Trainer / Annotator" },
  { value: "ai_content_creator", label: "AI Content Creator" },
  { value: "ai_experience_designer", label: "AI Experience Designer" },
  { value: "ai_assisted_designer", label: "AI-Assisted Designer:" },
  { value: "ai_music_audio_engineer", label: "AI Music & Audio Engineer" },
  { value: "ai_augmented_designer", label: "AI-Augmented Designer" },
  { value: "ai_healthcare_specialist", label: "AI in Healthcare Specialist" },
  { value: "ai_finance_specialist", label: "AI in Finance Specialist" },
  { value: "ai_education_designer", label: "AI in Education Designer" },
  { value: "ai_law_policy_advisor", label: "AI Law & Policy Advisor" },
  { value: "regulatory_ai_specialist", label: "Regulatory AI Specialist" },
  { value: "ai_security_specialist", label: "AI Security Specialist" },
  { value: "ai_agent_developer", label: "AI Agent Developer" },
];

export const CommunicationSkills = [
  { value: "active_listening", label: "Active Listening" },
  { value: "verbal_communication", label: "Verbal Communication" },
  { value: "written_communication", label: "Written Communication" },
  { value: "presentation_skills", label: "Presentation Skills" },
  { value: "non_verbal_communication", label: "Non-verbal Communication" },
];

export const InterpersonalTeamworkSkills = [
  { value: "empathy", label: "Empathy" },
  { value: "collaboration", label: "Collaboration" },
  { value: "conflict_resolution", label: "Conflict Resolution" },
  { value: "networking", label: "Networking" },
  { value: "cultural_competence", label: "Cultural Competence" },
];
export const CriticalThinkingProblemSolving = [
  { value: "analytical_thinking", label: "Analytical Thinking" },
  { value: "creative_thinking", label: "Creative Thinking" },
  { value: "decision_making", label: "Decision-Making" },
  { value: "adaptability", label: "Adaptability" },
  { value: "attention_to_detail", label: "Attention to Detail" },
];
export const SelfManagementSkills = [
  { value: "time_management", label: "Time Management" },
  { value: "accountability", label: "Accountability" },
  { value: "stress_management", label: "Stress Management" },
  { value: "self_motivation", label: "Self-Motivation" },
  { value: "growth_mindset", label: "Growth Mindset" },
];
export const LeadershipInfluence = [
  { value: "emotional_intelligence", label: "Emotional Intelligence (EQ)" },
  { value: "team_leadership", label: "Team Leadership" },
  { value: "influence_persuasion", label: "Influence & Persuasion" },
  { value: "delegation", label: "Delegation" },
  { value: "strategic_thinking", label: "Strategic Thinking" },
];

export const softSkills = [];

export const industries = [
  { value: "healthcare", label: "Healthcare" },
  { value: "finance_banking", label: "Finance & Banking" },
  { value: "government_public_sector", label: "Government & Public Sector" },
  { value: "retail_ecommerce", label: "Retail & E-commerce" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "automotive_transportation", label: "Automotive & Transportation" },
  { value: "education", label: "Education" },
  { value: "media_entertainment", label: "Media & Entertainment" },
  { value: "agriculture", label: "Agriculture" },
  {
    value: "human_resources_recruitment",
    label: "Human Resources & Recruitment",
  },
  { value: "construction_real_estate", label: "Construction & Real Estate" },
  { value: "legal", label: "Legal" },
  { value: "cybersecurity", label: "Cybersecurity" },
  { value: "life_sciences_biotech", label: "Life Sciences & Biotech" },
  { value: "energy_utilities", label: "Energy & Utilities" },
  { value: "logistics_supply_chain", label: "Logistics & Supply Chain" },
];

export const occupations = [
  { value: "software_engineer", label: "Software Engineer" },
  { value: "data_scientist", label: "Data Scientist" },
  { value: "ai_researcher", label: "AI Researcher" },
];
export const technologies = [
  {
    value: "cloud_computing",
    label: "Cloud Computing",
  },
  {
    value: "blockchain",
    label: "Blockchain",
  },
  {
    value: "cybersecurity",
    label: "Cybersecurity",
  },
];
export const selectYesNO = [
  {
    value: "",
    label: "Select",
  },
  {
    value: "yes",
    label: "Yes",
  },
  {
    value: "no",
    label: "No",
  },
];

export const Compliance = [
  { value: "regulatory_ai_specialist", label: "Regulatory AI Specialist" },
  { value: "ai_security_specialist", label: "AI Security Specialist" },
];

export const CreativeInterdisciplinaryRoles = [
  { value: "ai_content_creator", label: "AI Content Creator" },
  { value: "ai_experience_designer", label: "AI Experience Designer" },
  { value: "ai_assisted_designer", label: "AI-Assisted Designer:" },
  { value: "ai_music_audio_engineer", label: "AI Music & Audio Engineer" },
  { value: "ai_augmented_designer", label: "AI-Augmented Designer" },
  { value: "ai_healthcare_specialist", label: "AI in Healthcare Specialist" },
  { value: "ai_finance_specialist", label: "AI in Finance Specialist" },
  { value: "ai_education_designer", label: "AI in Education Designer" },
  { value: "ai_law_policy_advisor", label: "AI Law & Policy Advisor" },
];

export const BusinessStrategyDesignRoles = [
  { value: "ai_business_translator", label: "AI Business Translator" },
  { value: "ai_product_manager", label: "AI Product Manager" },
  { value: "ai_project_manager", label: "AI Project  Manager" },
  { value: "ai_ux_designer", label: "AI UX Designer" },
  { value: "ai_sales_engineer", label: "AI Sales Engineer" },
  { value: "ai_adoption_strategist", label: "AI Adoption Strategist:" },
  { value: "ai_strategy_consultant", label: "AI Strategy Consultant" },
  { value: "ai_systems_integrator", label: "AI Systems Integrator" },
  { value: "ai_compliance_manager", label: "AI Compliance Manager" },
  { value: "ai_business_analyst", label: "AI Business Analyst" },
  { value: "ai_trainer_annotator", label: "AI Trainer / Annotator" },
];

export const EthicsSafetyGovernanceRoles = [
  { value: "ai_ethicist", label: "AI Ethicist" },
  { value: "responsible_ai_specialist", label: "Responsible AI Specialist" },
  { value: "ai_ethics_auditor", label: "AI Ethics Auditor" },
  { value: "ai_safety_researcher", label: "AI Safety Researcher" },
];

export const InfrastructureMLOpsRoles = [
  { value: "data_engineer_ai_focused", label: "Data Engineer (AI-focused)" },
];

export const TechnicalDevelopmentRoles = [
  { value: "ai_ml_engineer", label: "AI/ML Engineer" },
  { value: "llm_fine_tuning_expert", label: "LLM Fine-Tuning Expert" },
  { value: "deep_learning_engineer", label: "Deep Learning Engineer" },
  { value: "computer_vision_engineer", label: "Computer Vision Engineer" },
  {
    value: "nlp_engineer",
    label: "Natural Language Processing (NLP) Engineer",
  },
  {
    value: "neural_architecture_designer",
    label: "Neural Architecture Designer",
  },
  { value: "synthetic_data_specialist", label: "Synthetic Data Specialist" },
  { value: "ai_research_scientist", label: "AI Research Scientist" },
  { value: "ai_explainability_engineer", label: "AI Explainability Engineer" },
  { value: "ai_prompt_engineer", label: "AI Prompt Engineer" },
  { value: "generative_ai_engineer", label: "Generative AI Engineer" },
  {
    value: "autonomous_systems_engineer",
    label: "Autonomous Systems Engineer",
  },
  { value: "ai_developer_advocate", label: "AI Developer Advocate" },
  { value: "mlops_engineer", label: "MLOps Engineer" },
  {
    value: "ai_infrastructure_architect",
    label: "AI Infrastructure Architect",
  },
  { value: "robotics_ai_engineer", label: "Robotics AI Engineer" },
  { value: "ai_solutions_architect", label: "AI Solutions Architect" },
];

export const raceColors = {
  White: "#FFC0CB",
  Asian: "#FFD700",
  "Black or African American": "#6495ED",
  "American Indian or Alaska Native": "#FF7F50",
  "Native Hawaiian or Other Pacific Islander": "#8A2BE2",
  "Two or More Races": "#20B2AA",
  "Other Race": "#A52A2A",
  "Hispanic or Latino (Ethnicity)": "#32CD32",
  "Prefer not to say": "#808080",
  Unknown: "#D3D3D3",
};

export const genderColors = {
  Male: "#3498db", // Blue
  Female: "#e91e63", // Pink
  "Prefer not to say": "#9b59b6", // Purple
  Others: "#2ecc71", // Green
};

export const colorClasses = {
  gray: {
    border: "border-gray-500",
    borderLight: "border-gray-200",
    bgLight: "bg-gray-50",
    text: "text-gray-700",
    bg: "bg-gray-600",
    hoverBg: "hover:bg-gray-700",
    buttonBg: "bg-gray-100",
    buttonText: "text-gray-700",
    buttonHover: "hover:bg-gray-200",
  },
  red: {
    border: "border-red-500",
    borderLight: "border-red-200",
    bgLight: "bg-red-50",
    text: "text-red-700",
    bg: "bg-red-600",
    hoverBg: "hover:bg-red-700",
    buttonBg: "bg-red-100",
    buttonText: "text-red-700",
    buttonHover: "hover:bg-red-200",
  },
  blue: {
    border: "border-blue-500",
    borderLight: "border-blue-200",
    bgLight: "bg-blue-50",
    text: "text-blue-700",
    bg: "bg-blue-600",
    hoverBg: "hover:bg-blue-700",
    buttonBg: "bg-blue-100",
    buttonText: "text-blue-700",
    buttonHover: "hover:bg-blue-200",
  },
  purple: {
    border: "border-purple-500",
    borderLight: "border-purple-200",
    bgLight: "bg-purple-50",
    text: "text-purple-700",
    bg: "bg-purple-600",
    hoverBg: "hover:bg-purple-700",
    buttonBg: "bg-purple-100",
    buttonText: "text-purple-700",
    buttonHover: "hover:bg-purple-200",
  },
};

export const groupType = [
  {
    value: "industry",
    label: "Industry",
  },
  {
    value: "learning",
    label: "Learning",
  },
];

export const timezones = Intl.supportedValuesOf("timeZone").map((tz) => ({
  value: tz,
  label: tz,
}));
